import React from "react";
import { ArrowLeft, ArrowRight, Check, ArrowUp } from "lucide-react";
import artieImage from "../../assets/images/artie.png";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useProjectStore } from "@/stores/projectStore";
import { ArtieScoreData } from "@/types/artie-score";
import { Skeleton } from "@/components/ui/skeleton";
import { useParams, useNavigate, useLocation } from "react-router-dom";

interface ScoreStatusResponse {
  score_data: ArtieScoreData;
  // Add other properties from the status response if needed
}

const ScriptFeedbackPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const showContinueButton = location.state?.showContinueButton ?? true;

  const { data: scoreStatus, isLoading, error } = useQuery<ScoreStatusResponse>({
    queryKey: ['artieScoreStatus', projectId],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is missing");
      }
      return apiClient.getArtieScoreStatus(projectId) as Promise<ScoreStatusResponse>;
    },
    enabled: !!projectId,
  });

  const artieScore = scoreStatus?.score_data;
  const craftAssessment = artieScore?.brand_fit_analysis?.craft_assessment;

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">
        <div className="w-full max-w-5xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Header & Introduction */}
          <div className="flex flex-col items-center text-center max-w-3xl mb-12">
            {/* Logo */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
              <img
                src={artieImage}
                alt="Artie"
                className="w-48 h-48 object-contain relative z-10"
              />
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              Here’s what we noticed.
            </h1>

            <div className="space-y-1 text-[oklch(0.55_0_0)] text-lg leading-relaxed font-ibm max-w-2xl">
              <p>
                This is a high-level snapshot from a professional writer review.
                It highlights what’s landing well and a few areas worth
                attention if you want to move closer to your goal. We’ll get
                into more details later.
              </p>
            </div>
          </div>

          {/* Script Feedback Card */}
          <div className="w-full max-w-3xl bg-white rounded-3xl p-8 md:p-10 mb-12 border border-gray-200 shadow-sm">
            {/* Card Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 border-b border-gray-100 pb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Script Feedback
              </h2>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wide mt-2 sm:mt-0">
                Industry-calibrated review
              </span>
            </div>

            {isLoading && (
              <div className="space-y-8">
                <Skeleton className="h-24" />
                <Skeleton className="h-24" />
                <Skeleton className="h-12" />
              </div>
            )}
            {error && <p className="text-red-500">Error: {error.message}</p>}

            {!isLoading && !error && craftAssessment && (
              <div className="space-y-8">
                {/* What's working Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                      <Check className="w-4 h-4" />
                    </div>
                    <h3 className="font-semibold text-gray-900">
                      What's working
                    </h3>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6 text-gray-700 leading-relaxed text-sm md:text-base border border-gray-100">
                    {(() => {
                      const brandFit = artieScore?.brand_fit_analysis;
                      const strengths = craftAssessment.specific_strengths || [];
                      const hasStrongCraft = craftAssessment.overall_craft_level === 'strong';
                      const hasGoodBrandFit = brandFit?.brand_safety?.safe_for_brands && brandFit.brand_safety.risk_level === 'low';
                      const integrationCount = brandFit?.integration_opportunities?.natural_integration_points?.length || 0;

                      // Build personalized feedback based on actual strengths
                      const strengthMessages = [];

                      if (hasStrongCraft && strengths.length > 0) {
                        // Use actual strengths from the analysis
                        const topStrength = strengths[0].toLowerCase();
                        strengthMessages.push(`Your ${topStrength} stands out.`);
                      } else if (hasStrongCraft) {
                        strengthMessages.push("Your storytelling is solid.");
                      }

                      if (hasGoodBrandFit && integrationCount > 0) {
                        strengthMessages.push(`We spotted ${integrationCount} natural moments where brands could fit in without disrupting the narrative.`);
                      } else if (hasGoodBrandFit) {
                        strengthMessages.push("Your content is brand-safe and has commercial appeal.");
                      }

                      if (strengthMessages.length === 0) {
                        strengthMessages.push("Your script shows promise. We see potential in the concept and the direction you're taking.");
                      }

                      return <p>{strengthMessages.join(" ")}</p>;
                    })()}
                  </div>
                </div>

                {/* Where to focus Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                      <ArrowUp className="w-4 h-4" />
                    </div>
                    <h3 className="font-semibold text-gray-900">
                      Where to focus next based on your goal
                    </h3>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6 text-gray-700 leading-relaxed text-sm md:text-base border border-gray-100">
                    {(() => {
                      const brandFit = artieScore?.brand_fit_analysis;
                      const weaknesses = craftAssessment.specific_weaknesses || [];
                      const gaps = artieScore?.production_readiness_analysis?.gaps || [];

                      // Use new V2 tactics recommendations (prioritized by the backend)
                      const currentRecommendations = (artieScore?.current_recommendations || []).filter(rec => !rec.is_hidden);
                      const topTacticRecommendation = currentRecommendations.find(r => r.priority_rank === 1);

                      // Fallback to legacy recommendations if V2 not available
                      const legacyRecommendations = artieScore?.recommendations || [];
                      const topLegacyRecommendation = legacyRecommendations.find(r => r.priority === 1);

                      const needsCraftWork = craftAssessment.overall_craft_level === 'needs_work';
                      const hasBrandIssues = brandFit?.brand_safety?.risk_level === 'high' || brandFit?.brand_safety?.risk_level === 'medium';
                      const brandConcerns = brandFit?.brand_safety?.concerns || [];

                      // Build personalized, actionable feedback
                      const focusMessages = [];

                      // Priority 1: Use top V2 tactic recommendation if available
                      if (topTacticRecommendation) {
                        // Create specific, actionable message based on the actual tactic
                        const tacticId = topTacticRecommendation.tactic_id;
                        const tacticName = topTacticRecommendation.tactic_name;

                        // Provide specific, actionable guidance based on the tactic
                        const tacticMessages = {
                          // Reach tactics
                          'R1': 'Consider investing in paid promotion to accelerate your audience growth. Even a modest budget can significantly expand your reach.',
                          'R2': 'Focus on optimizing your content hooks, thumbnails, and posting schedule. Small improvements in these areas can boost your organic growth.',
                          'R3': 'Look for partnership opportunities with established platforms or channels that align with your content. This can provide immediate access to larger audiences.',
                          'R4': 'Consider collaborating with other creators in your space. Cross-promotion can help you reach new audiences organically.',
                          'R5': 'Attach recognizable talent or personalities to your project. This can significantly boost your reach potential and credibility.',
                          'R6': 'Create a proof of concept or pilot episode to demonstrate your content\'s appeal and reach potential to partners.',
                          'R7': 'Leverage your existing content library by repurposing, updating, or creating spin-offs that can attract new viewers.',
                          'R8': 'Start by targeting a specific niche audience before expanding broadly. This focused approach often leads to stronger engagement.',
                          'R9': 'Strengthen your opening moments to better capture and hold viewer attention from the very beginning.',
                          'R10': 'Add moments in your content that are easily shareable on social media to increase organic reach.',
                          'R11': 'Improve your content structure and pacing to keep viewers engaged throughout the entire experience.',
                          'R12': 'Plan strategic placement opportunities for potential sponsors to make your content more attractive to advertisers.',

                          // Brand fit tactics
                          'BF1': 'Focus on clarifying your core story and message. A clear, compelling narrative makes your content more attractive to brand partners.',
                          'BF2': 'Strengthen your opening hook to better engage audiences and demonstrate your content\'s commercial appeal.',
                          'BF3': 'Establish a consistent tone throughout your content that aligns with brand-safe messaging.',
                          'BF4': 'Optimize your pacing to maintain audience engagement and create better integration opportunities.',
                          'BF5': 'Improve your dialogue quality to make your content more professional and brand-friendly.',
                          'BF6': 'Clarify your story\'s resolution to ensure audiences feel satisfied and brands see clear value.',
                          'BF7': 'Develop stronger character motivations to create more compelling content that brands want to associate with.',
                          'BF8': 'Add natural moments where brands could integrate without disrupting your story flow.',
                          'BF9': 'Expand the types of brand integration opportunities in your content to appeal to more potential partners.',
                          'BF10': 'Address any content elements that might concern brands to make your project more partnership-friendly.',
                          'BF11': 'Explore alternative brand categories that might be a better fit for your content and audience.',
                          'BF12': 'Consider adjusting your flexibility preferences to open up more brand partnership opportunities.',

                          // Production readiness tactics
                          'PR1': 'Add specific production dates and timelines to show brands you have a clear execution plan.',
                          'PR2': 'Provide clear budget information to help potential partners understand the investment required.',
                          'PR3': 'Attach key production team members to demonstrate you have the expertise needed for professional execution.',
                          'PR4': 'Upload examples of your past brand partnership work to build credibility with potential sponsors.',
                          'PR5': 'Add your production credits and experience to establish your track record and professionalism.',
                          'PR6': 'Partner with an experienced collaborator or producer to reduce perceived risk for brand partners.',
                          'PR7': 'Ensure your production scope aligns with your stated budget to demonstrate realistic planning.',
                          'PR8': 'Develop contingency plans to show brands you\'ve thought through potential challenges.',
                          'PR9': 'Secure key filming locations to demonstrate your project is ready for production.',
                          'PR10': 'Get letters of intent from key participants to show commitment and reduce execution risk.',
                          'PR11': 'Create a sizzle reel or proof of concept to visually demonstrate your project\'s potential.',
                          'PR12': 'Secure production insurance and bonding to meet professional partnership requirements.'
                        };

                        const specificMessage = tacticMessages[tacticId];
                        if (specificMessage) {
                          focusMessages.push(specificMessage);
                        } else {
                          // Fallback to generic message if specific tactic not found
                          focusMessages.push(`Focus on "${tacticName.toLowerCase()}" to move your project closer to your goals.`);
                        }
                      }
                      // Priority 2: Fallback to legacy recommendation
                      else if (topLegacyRecommendation) {
                        focusMessages.push(topLegacyRecommendation.why_it_matters);
                      }
                      // Priority 3: Address specific weaknesses
                      else if (weaknesses.length > 0) {
                        const weakness = weaknesses[0].toLowerCase();
                        focusMessages.push(`Consider strengthening your ${weakness}. This will make your story more compelling and easier for brands to connect with.`);
                      }
                      // Priority 4: Address brand safety concerns
                      else if (hasBrandIssues && brandConcerns.length > 0) {
                        focusMessages.push(`Address ${brandConcerns[0].toLowerCase()} to make your project more brand-friendly. Small adjustments can open up more partnership opportunities.`);
                      }
                      // Priority 5: Address production gaps
                      else if (gaps.length > 0) {
                        const gap = gaps[0].toLowerCase();
                        focusMessages.push(`Focus on ${gap}. This will strengthen your project's readiness and appeal to potential partners.`);
                      }
                      // Priority 6: Generic positive message
                      else {
                        focusMessages.push("Continue refining your story and building out your production plan. We'll help you find the perfect brand partners as you progress.");
                      }

                      return <p>{focusMessages.join(" ")}</p>;
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer & Navigation */}
        <div className="w-full mt-auto pb-10 px-6 md:px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              <Button
                variant="ghost"
                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                onClick={() => window.history.back()}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            <div className="flex-1 flex justify-end">
              {/* Placeholder for future navigation */}
              {showContinueButton && (
                <Button
                  className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => navigate(`/project-v2/score-v2/${projectId}`)}
                  disabled={isLoading}
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ScriptFeedbackPage;
